<template>
  <div class="doughnut-chart-container">
    <canvas :id="canvasId"></canvas>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'

export default {
  name: 'DoughnutChart',
  props: {
    chartId: {
      type: String,
      default: () => 'doughnut-chart-' + Math.random().toString(36).substr(2, 9)
    },
    chartData: {
      type: Object,
      required: true,
      default: () => ({
        labels: [],
        datasets: []
      })
    },
    chartOptions: {
      type: Object,
      default: () => ({})
    },
    width: {
      type: Number,
      default: 400
    },
    height: {
      type: Number,
      default: 400
    }
  },
  data () {
    return {
      chart: null,
      uniqueId: 'doughnut-chart-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
      defaultOptions: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '65%', // Creates the doughnut hole
        layout: {
          padding: 10
        },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              padding: 15,
              usePointStyle: true,
              font: {
                size: 12
              },
              generateLabels: function (chart) {
                const data = chart.data
                if (data.labels.length && data.datasets.length) {
                  const dataset = data.datasets[0]
                  const total = dataset.data.reduce((a, b) => a + b, 0)
                  return data.labels.map((label, i) => {
                    const value = dataset.data[i]
                    const percentage = ((value / total) * 100).toFixed(1)
                    return {
                      text: `${label}: ${percentage}%`,
                      fillStyle: dataset.backgroundColor[i],
                      hidden: false,
                      index: i
                    }
                  })
                }
                return []
              }
            }
          },
          tooltip: {
            enabled: true,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: '#ddd',
            borderWidth: 1,
            titleFont: {
              size: 14,
              weight: 'bold'
            },
            bodyFont: {
              size: 13
            },
            padding: 10,
            cornerRadius: 4,
            displayColors: true,
            callbacks: {
              label: function (context) {
                const label = context.label || ''
                const value = context.parsed
                const dataset = context.dataset
                const total = dataset.data.reduce((a, b) => a + b, 0)
                const percentage = ((value / total) * 100).toFixed(1)
                return `${label}: ${value.toLocaleString()} (${percentage}%)`
              }
            }
          }
        },
        elements: {
          arc: {
            borderWidth: 2,
            borderColor: '#fff',
            hoverBorderWidth: 3,
            hoverBorderColor: '#fff'
          }
        },
        animation: {
          animateRotate: true,
          animateScale: false
        }
      }
    }
  },
  computed: {
    mergedOptions () {
      return this.deepMerge(this.defaultOptions, this.chartOptions)
    },
    canvasId () {
      return this.chartId || this.uniqueId
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler () {
        this.updateChart()
      }
    },
    chartOptions: {
      deep: true,
      handler () {
        this.updateChart()
      }
    }
  },
  mounted () {
    this.renderChart()
  },
  beforeDestroy () {
    if (this.chart) {
      this.chart.destroy()
    }
  },
  methods: {
    renderChart () {
      this.$nextTick(() => {
        const ctx = document.getElementById(this.canvasId)
        if (!ctx) return

        // Check if a chart already exists on this canvas
        const existingChart = Chart.getChart(ctx)
        if (existingChart) {
          existingChart.destroy()
        }

        // Destroy our existing chart instance if it exists
        if (this.chart) {
          this.chart.destroy()
          this.chart = null
        }

        // Create new chart
        try {
          this.chart = new Chart(ctx, {
            type: 'doughnut',
            data: this.chartData,
            options: this.mergedOptions
          })
        } catch (error) {
          console.error('Error creating doughnut chart:', error)
        }
      })
    },
    updateChart () {
      if (!this.chart) {
        this.renderChart()
        return
      }

      // Update chart data
      this.chart.data = this.chartData
      this.chart.options = this.mergedOptions
      this.chart.update('active')
    },
    deepMerge (target, source) {
      const output = Object.assign({}, target)
      if (this.isObject(target) && this.isObject(source)) {
        Object.keys(source).forEach(key => {
          if (this.isObject(source[key])) {
            if (!(key in target)) {
              Object.assign(output, { [key]: source[key] })
            } else {
              output[key] = this.deepMerge(target[key], source[key])
            }
          } else {
            Object.assign(output, { [key]: source[key] })
          }
        })
      }
      return output
    },
    isObject (item) {
      return item && typeof item === 'object' && !Array.isArray(item)
    }
  }
}
</script>

<style scoped>
.doughnut-chart-container {
  position: relative;
  width: 100%;
  height: 100%;
}

canvas {
  max-width: 100%;
  max-height: 100%;
}
</style>
